import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface PlatformBooking {
  bookingId: string;
  customerId: string;
  providerId: string;
  serviceType: string;
  status: 'pending' | 'confirmed' | 'paid' | 'in_progress' | 'completed' | 'cancelled';
  scheduledDate: string;
  scheduledTime: string;
  duration?: number;
  pricing: {
    rate: number;
    currency: string;
    type: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'per_service';
    distanceBreakdown?: {
      distance: number;
      baseRate: number;
      distanceCharge: number;
      transportCharge: number;
      total: number;
    };
  };
  paymentStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  recurringConfig?: {
    frequency: 'weekly' | 'monthly';
    endDate?: string;
    occurrences?: number;
  };
  negotiationStatus: 'none' | 'proposed' | 'countered' | 'accepted' | 'rejected' | 'expired';
  proposedRate?: number;
  originalRate: number;
  finalRate?: number;
  distance?: number;
  serviceAddress?: {
    address: string;
    city: string;
    state: string;
    pincode: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface BookingListResponse {
  bookings: PlatformBooking[];
  count: number;
  lastEvaluatedKey?: Record<string, unknown>;
}

export interface BookingNegotiationsResponse {
  negotiation: unknown | null;
  count: number;
}

export interface BookingPaymentsResponse {
  payments: unknown[];
  count: number;
}

export interface ListBookingsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  status?: string;
  customerId?: string;
  providerId?: string;
  startDate?: string;
  endDate?: string;
}

class BookingManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listBookings(params?: ListBookingsParams): Promise<BookingListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.customerId) queryParams.append('customerId', params.customerId);
    if (params?.providerId) queryParams.append('providerId', params.providerId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await this.api.get<{success: boolean; data: BookingListResponse}>(
      `/admin/bookings?${queryParams.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to list bookings');
    }

    return response.data.data;
  }

  async getBooking(bookingId: string): Promise<PlatformBooking> {
    const response = await this.api.get<{success: boolean; data: {booking: PlatformBooking}}>(
      `/admin/bookings/${bookingId}`
    );

    if (!response.data.success || !response.data.data?.booking) {
      throw new Error('Failed to get booking');
    }

    return response.data.data.booking;
  }

  async updateBooking(bookingId: string, updates: Partial<PlatformBooking>): Promise<PlatformBooking> {
    try {
      const response = await this.api.put<{success: boolean; data: {booking: PlatformBooking}}>(
        `/admin/bookings/${bookingId}`,
        updates
      );

      const booking = response.data?.data?.booking;
      if (!response.data?.success || !booking) {
        throw new Error('Invalid response from server');
      }

      return booking;
    } catch (err: unknown) {
      const message = this.extractErrorMessage(err, 'Failed to update booking');
      console.error('[updateBooking] API error:', {bookingId, err, message});
      throw new Error(message);
    }
  }

  async cancelBooking(bookingId: string): Promise<PlatformBooking> {
    try {
      const response = await this.api.delete<{success: boolean; data: {booking: PlatformBooking}}>(
        `/admin/bookings/${bookingId}`
      );

      const booking = response.data?.data?.booking;
      if (!response.data?.success || !booking) {
        throw new Error('Invalid response from server');
      }

      return booking;
    } catch (err: unknown) {
      const message = this.extractErrorMessage(err, 'Failed to cancel booking');
      console.error('[cancelBooking] API error:', {bookingId, err, message});
      throw new Error(message);
    }
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as {response?: {data?: {error?: {message?: string}; message?: string}; status?: number}};
      const data = axiosErr.response?.data;
      return (
        data?.error?.message ??
        (typeof data?.message === 'string' ? data.message : null) ??
        (axiosErr.response?.status === 401 ? 'Session expired. Please log in again.' : null) ??
        (axiosErr.response?.status === 403 ? 'You do not have permission to perform this action.' : null) ??
        fallback
      );
    }
    return err instanceof Error ? err.message : fallback;
  }

  async getBookingNegotiations(bookingId: string): Promise<BookingNegotiationsResponse> {
    const response = await this.api.get<{success: boolean; data: BookingNegotiationsResponse}>(
      `/admin/bookings/${bookingId}/negotiations`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get booking negotiations');
    }

    return response.data.data;
  }

  async getBookingPayments(bookingId: string): Promise<BookingPaymentsResponse> {
    const response = await this.api.get<{success: boolean; data: BookingPaymentsResponse}>(
      `/admin/bookings/${bookingId}/payments`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get booking payments');
    }

    return response.data.data;
  }
}

export const bookingManagementService = new BookingManagementService();

